import { useCallback, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { format, formatDistanceToNow } from "date-fns";

import {
  DEFAULT_API_KEY_NAME,
  apiKeyErrorMessage,
  createBrevAPIKey,
  defaultAPIKeyName,
  defaultKeyExpirationDate,
  endOfDay,
  isBrevAPIKey,
  maximumKeyExpirationDate,
} from "../utils";

interface UseAPIKeyStepOptions {
  activeOrgId: string | undefined;
  computeName: string;
  getComputeNameError: (value: string) => string;
  hasValidName: boolean;
  onComputeNameChange: (value: string, completeImmediately?: boolean) => void;
  onComputeNameValidationError: (message: string) => void;
}

export const useAPIKeyStep = ({
  activeOrgId,
  computeName,
  getComputeNameError,
  hasValidName,
  onComputeNameChange,
  onComputeNameValidationError,
}: UseAPIKeyStepOptions) => {
  const [apiKeyName, setAPIKeyName] = useState(DEFAULT_API_KEY_NAME);
  const [isEditingAPIKeyName, setIsEditingAPIKeyName] = useState(false);
  const [keyExpirationDate, setKeyExpirationDate] = useState(
    defaultKeyExpirationDate,
  );
  const [isEditingKeyExpirationDate, setIsEditingKeyExpirationDate] =
    useState(false);
  const [isKeyExpirationDatePickerOpen, setIsKeyExpirationDatePickerOpen] =
    useState(false);
  const [previousOrgId, setPreviousOrgId] = useState(activeOrgId);

  const createAPIKeyMutation = useMutation({
    mutationFn: async (variables: {
      expirationDate: Date;
      name: string;
      orgId: string;
    }) => {
      if (variables.expirationDate.getTime() <= Date.now()) {
        throw new Error("Choose an expiration date in the future.");
      }

      if (
        variables.expirationDate.getTime() >
        maximumKeyExpirationDate().getTime()
      ) {
        throw new Error("Choose an expiration date within the next 90 days.");
      }

      return createBrevAPIKey({
        expirationDate: variables.expirationDate,
        name: variables.name,
        orgId: variables.orgId,
      });
    },
  });

  if (previousOrgId !== activeOrgId) {
    setPreviousOrgId(activeOrgId);
    setAPIKeyName(DEFAULT_API_KEY_NAME);
    setIsEditingAPIKeyName(false);
    setKeyExpirationDate(defaultKeyExpirationDate());
    setIsEditingKeyExpirationDate(false);
    setIsKeyExpirationDatePickerOpen(false);
    createAPIKeyMutation.reset();
  }

  const handleComputeNameChange = useCallback(
    (value: string, completeImmediately = false) => {
      onComputeNameChange(value, completeImmediately);
      setAPIKeyName((currentName) =>
        currentName === defaultAPIKeyName(computeName)
          ? defaultAPIKeyName(value)
          : currentName,
      );
    },
    [computeName, onComputeNameChange],
  );

  const createNewAPIKey = useCallback(async () => {
    const computeNameError = getComputeNameError(computeName);

    if (computeNameError) {
      onComputeNameValidationError(computeNameError);
      return;
    }

    if (!activeOrgId || !apiKeyName.trim() || createAPIKeyMutation.isPending) {
      return;
    }

    await createAPIKeyMutation.mutateAsync({
      expirationDate: keyExpirationDate,
      name: apiKeyName.trim(),
      orgId: activeOrgId,
    });
  }, [
    activeOrgId,
    apiKeyName,
    createAPIKeyMutation,
    computeName,
    getComputeNameError,
    keyExpirationDate,
    onComputeNameValidationError,
  ]);

  const handleKeyExpirationDateChange = useCallback(
    (date: Date | undefined) => {
      if (!date) return;

      setKeyExpirationDate(endOfDay(date));
      createAPIKeyMutation.reset();
    },
    [createAPIKeyMutation],
  );

  const newAPIKey = createAPIKeyMutation.data ?? "";
  const hasCompletedStepTwo = isBrevAPIKey(newAPIKey);
  const keyExpirationDateLabel = `${format(
    keyExpirationDate,
    "dd MMM, yyyy",
  )} at 11:59PM (${formatDistanceToNow(keyExpirationDate, {
    addSuffix: true,
  })})`;
  const canCreateNewKey = Boolean(
    activeOrgId &&
    hasValidName &&
    apiKeyName.trim() &&
    !newAPIKey &&
    !createAPIKeyMutation.isPending,
  );
  const createKeyError = createAPIKeyMutation.error
    ? apiKeyErrorMessage(createAPIKeyMutation.error)
    : "";

  return {
    apiKeyName,
    canCreateNewKey,
    createKeyError,
    createNewAPIKey,
    handleComputeNameChange,
    handleKeyExpirationDateChange,
    hasCompletedStepTwo,
    hasValidAPIKey: hasCompletedStepTwo,
    isCreatingKey: createAPIKeyMutation.isPending,
    isEditingAPIKeyName,
    isEditingKeyExpirationDate,
    isKeyExpirationDatePickerOpen,
    keyExpirationDate,
    keyExpirationDateLabel,
    newAPIKey,
    onAPIKeyNameChange: setAPIKeyName,
    onStartEditingAPIKeyName: () => setIsEditingAPIKeyName(true),
    onStartEditingKeyExpirationDate: () => {
      setIsEditingKeyExpirationDate(true);
      setIsKeyExpirationDatePickerOpen(true);
    },
    onKeyExpirationDatePickerOpenChange: setIsKeyExpirationDatePickerOpen,
  };
};
